import React, { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import { toast } from 'react-toastify';

import services from 'api/services';
import { editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { useDetectOutsideClick } from 'hooks/useDetectOutsideClick';
import { useAppContext, useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';
import {
  DescriptionContainer,
  DescriptionScrollContainer,
  EditorRefSpan,
  DescriptionText,
  QuillContainer,
} from './styles';

Quill.register('modules/imageResize', ImageResize);

const { FreecodeCreatorService } = services;

const editorFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
  'align',
  'code-block',
  'image',
  'width',
  'video',
];

export default function FreecodeText({ text, textRaw, textRawEdit, titleElement = null, onCopy = () => {} }) {
  const { editorType } = useFreecodeContextLayout();
  const { testImpl } = useFreecodeContextData();
  const { isDarkMode } = useAppContext();

  const [offsetHeight, setOffsetHeight] = useState('0px');

  const quillRef = useRef(null);
  const editorRef = useRef(null);
  const [displayQuill, setDisplayQuill] = useDetectOutsideClick(editorRef, false);

  useEffect(() => setOffsetHeight(`${titleElement?.ref?.current?.clientHeight || 0}px`), [titleElement]);

  const imageHandler = useCallback(() => {
    const quillObj = quillRef?.current?.getEditor?.();
    const range = quillObj?.getSelection?.();
    if (!quillObj || !range) toast.error('upload did not find editor');

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.onchange = async () => {
      try {
        await new Promise((resolve, reject) => {
          try {
            const file = input?.files?.[0];
            if (!file) reject('No file selected');
            const reader = new FileReader();

            reader.onload = async () => {
              try {
                let response = await FreecodeCreatorService.uploadDescriptionImage(testImpl?.test, testImpl?._id, {
                  data: reader.result,
                  uploadType: 'graphicsImage',
                  imageName: file.name,
                });
                if (response.error) return reject(response.error?.message || 'Upload returned an error');
                if (!response?.imageData?.url) return reject('Upload response missing image url');

                quillObj.editor.insertEmbed(range.index, 'image', response.imageData?.url);

                // Force update text since insertEmbed does not trigger onChange
                if (textRawEdit && quillObj.container?.firstChild?.innerHTML)
                  textRawEdit(quillObj.container?.firstChild?.innerHTML);

                resolve();
              } catch (e) {
                reject(e);
              }
            };

            reader.onerror = () => reject('Could Not Read File');

            reader.readAsDataURL(file);
          } catch (e) {
            reject(e);
          }
        });
      } catch (error) {
        if (typeof error === 'string') toast.error(error);
        else toast.error(typeof error?.message === 'string' ? error.message : 'Image upload failed');
      }
    };
    input.click();
  }, [testImpl?._id, testImpl?.test, textRawEdit]);

  const EditorModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: '1' }, { header: '2' }],
          ['bold', 'italic', 'underline'],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['code-block', 'link', 'image', 'video', 'clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        // toggle to add extra line breaks when pasting HTML:
        matchVisual: false,
      },
      imageResize: {
        displaySize: true,
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
      },
    }),
    [imageHandler]
  );

  const onTextClick = () => {
    if (editorType !== editorTypes.creator) return;
    titleElement?.ref?.current?.scrollIntoView?.();
    setDisplayQuill(true);
  };

  return (
    <DescriptionContainer>
      <DescriptionScrollContainer hideOverflow={displayQuill} onCopy={onCopy}>
        {titleElement}
        <EditorRefSpan ref={editorRef} isDarkMode={isDarkMode} displayQuill={displayQuill}>
          {displayQuill ? (
            <QuillContainer offsetHeight={offsetHeight}>
              <ReactQuill
                ref={quillRef}
                value={textRaw}
                onChange={textRawEdit}
                modules={EditorModules}
                formats={editorFormats}
                theme="snow"
                preserveWhitespace={true}
              />
            </QuillContainer>
          ) : (
            <DescriptionText dangerouslySetInnerHTML={{ __html: text }} onClick={onTextClick} />
          )}
        </EditorRefSpan>
      </DescriptionScrollContainer>
    </DescriptionContainer>
  );
}
